
import { ArrowRight, Clock, Building, Users, Award } from "lucide-react";
import { 
  Carousel, 
  CarouselContent, 
  CarouselItem,
  CarouselPrevious,
  CarouselNext 
} from "@/components/ui/carousel";
import { useState } from "react";

type HistoryEvent = {
  year: string;
  description: string;
  type: 'creation' | 'expansion' | 'acquisition' | 'innovation';
};

const HistoireTimeline = () => {
  const [api, setApi] = useState<any>();
  
  // Timeline events organized chronologically with types
  const timelineEvents: HistoryEvent[] = [
    { year: "1987", description: "Création de Fabrude Recyclage dans le Cantal, première étape du Groupe Fabre", type: "creation" },
    { year: "2000", description: "Expansion des activités et développement du pôle environnement", type: "expansion" },
    { year: "2010", description: "Atteinte de 50% de taux de valorisation des déchets traités", type: "innovation" },
    { year: "2021", description: "Rachat de Bellé Recyclage dans l'Aveyron, renforcement de notre présence régionale", type: "acquisition" },
    { year: "2023", description: "Rachat de Lescout Recyclage dans le Tarn, extension vers l'est de notre territoire", type: "acquisition" },
    { year: "2023", description: "Ouverture de RM Recyclage dans le Cantal, consolidation de notre implantation historique", type: "expansion" },
    { year: "2024", description: "Rachat de Briane Jean dans l'Aveyron, densification du réseau de collecte", type: "acquisition" },
    { year: "2024", description: "Rachat de la Casse Auto du Bassin (Aveyron), diversification de notre offre", type: "acquisition" },
    { year: "2024", description: "Lancement de Valocap, notre plateforme dédiée à l'économie circulaire", type: "innovation" },
    { year: "2025", description: "Rachat de Profit Recyclage à Paris, première implantation en Île-de-France", type: "expansion" },
    { year: "2025", description: "Rachat de la Carmausine de Récupération à Carmaux, renforcement de notre présence dans le Tarn", type: "acquisition" },
  ];

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'creation': return <Building className="h-5 w-5 text-fabre-green" />;
      case 'expansion': return <ArrowRight className="h-5 w-5 text-fabre-teal" />;
      case 'acquisition': return <Users className="h-5 w-5 text-fabre-gold" />;
      case 'innovation': return <Award className="h-5 w-5 text-fabre-darkteal" />;
      default: return <Clock className="h-5 w-5 text-fabre-green" />;
    }
  };

  const getEventColor = (type: string) => {
    switch (type) {
      case 'creation': return 'border-fabre-green bg-fabre-green/5';
      case 'expansion': return 'border-fabre-teal bg-fabre-teal/5';
      case 'acquisition': return 'border-fabre-gold bg-fabre-gold/5';
      case 'innovation': return 'border-fabre-darkteal bg-fabre-darkteal/5';
      default: return 'border-fabre-green bg-fabre-green/5';
    }
  };

  return (
    <div className="max-w-6xl mx-auto mt-20">
      <div className="text-center mb-16">
        <h2 className="text-4xl font-heading font-bold mb-6 text-fabre-gray">
          Notre chronologie
        </h2>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Découvrez les étapes clés qui ont marqué notre développement
        </p>
      </div>
      
      {/* Mobile Timeline View - Enhanced Carousel */}
      <div className="md:hidden mb-12">
        <Carousel
          setApi={setApi}
          className="w-full"
          opts={{
            align: "start",
          }}
        >
          <CarouselContent>
            {timelineEvents.map((event, index) => (
              <CarouselItem key={index} className="basis-full">
                <div className={`p-6 rounded-2xl shadow-lg border-l-4 ${getEventColor(event.type)} hover:shadow-xl transition-all duration-300`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="bg-fabre-green text-white py-2 px-4 font-bold rounded-full text-lg">
                      {event.year}
                    </div>
                    {getEventIcon(event.type)}
                  </div>
                  <p className="text-fabre-gray leading-relaxed">{event.description}</p>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="left-2" />
          <CarouselNext className="right-2" />
        </Carousel>
      </div>
      
      {/* Desktop Timeline View - Enhanced */}
      <div className="hidden md:block relative">
        <div className="absolute h-full w-1 bg-gradient-to-b from-fabre-green via-fabre-teal to-fabre-darkteal left-1/2 transform -translate-x-1/2 rounded-full"></div>
        
        {timelineEvents.map((event, index) => (
          <div 
            key={index} 
            className={`mb-16 flex flex-row items-center ${index % 2 === 0 ? 'md:flex-row-reverse' : ''} hover:scale-105 transition-transform duration-300`}
          >
            <div className="w-1/2 px-8 mb-0 text-center">
              <div className="bg-gradient-to-r from-fabre-green to-fabre-teal text-white py-3 px-6 inline-block font-bold rounded-full text-xl shadow-lg">
                {event.year}
              </div>
            </div>
            <div className="z-10 w-12 h-12 bg-white border-4 border-fabre-green rounded-full flex items-center justify-center shadow-lg">
              {getEventIcon(event.type)}
            </div>
            <div className="w-1/2 px-8">
              <div className={`p-6 rounded-2xl shadow-lg border-l-4 ${getEventColor(event.type)} hover:shadow-xl transition-all duration-300`}>
                <p className="text-fabre-gray leading-relaxed text-lg">{event.description}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HistoireTimeline;
